import { fromMinorUnits } from '@/api/map/agent'
import {
  fromMinorUnitsSigned,
  isUnavailable,
  type AnalyticsDailyResponse,
  type AnalyticsUnavailable,
} from '@/api/map/pnl'
import type { PnlSummary } from '@/data/api'
import type { PnlRow } from '@/data/types'
import { round2 } from '@/lib/book'

/** analytics-service Money — minor units as string. */
export type MoneyMinor = string

export interface AnalyticsCommissionRow {
  date: string
  agentId: string
  agentName?: string
  commissionMinor: MoneyMinor
  direction?: 'earned' | 'paid'
  source: 'settlement_postings'
}

export interface AnalyticsCommissionResponse {
  asOf?: string
  martBuiltAt?: string | null
  martStale?: boolean
  rows: AnalyticsCommissionRow[]
}

export type CommissionRowView = {
  id: string
  date: string
  agentId: string
  agentName: string
  commission: number
  direction: 'earned' | 'paid'
  source: 'settlement_postings'
}

export type CommissionDailyPoint = {
  date: string
  commission: number
  netPnl: number
  turnover: number
  bets: number
}

/**
 * Console view of GET /commission (+ /daily for the book-net line).
 * Until slice a6 the API returns 503 Unavailable — never invent commission.
 */
export type CommissionReport = {
  asOf?: string
  martStale?: boolean
  /** Set when live analytics answered 503 with Unavailable (slice a6). */
  unavailable?: AnalyticsUnavailable
  /** Ledger rows from a 200 response. Empty when unavailable or fixture-event mode. */
  rows: CommissionRowView[]
  /**
   * Fixture-only event-level commission rows (getPnl). Null on the live path —
   * the OpenAPI CommissionRow has no event/market/member/sport fields.
   */
  eventRows: PnlRow[] | null
  daily: CommissionDailyPoint[]
  totals: {
    commission: number
    earned: number
    paid: number
    turnover: number
    netPnl: number
    bets: number
  }
  /** Live analytics: sport/game breakouts are not on /commission. */
  sportGameUnavailable: boolean
}

export function toCommissionRowView(row: AnalyticsCommissionRow, index: number): CommissionRowView {
  const direction = row.direction === 'paid' ? 'paid' : 'earned'
  return {
    id: `${row.date}:${row.agentId}:${direction}:${index}`,
    date: row.date,
    agentId: row.agentId,
    agentName: row.agentName?.trim() || row.agentId.slice(0, 8),
    commission: fromMinorUnits(row.commissionMinor),
    direction,
    source: 'settlement_postings',
  }
}

function dailyFromAnalytics(
  daily: AnalyticsDailyResponse | undefined,
  commissionByDate: Map<string, number>,
): CommissionDailyPoint[] {
  return (daily?.rows ?? []).map((d) => ({
    date: d.date,
    commission: commissionByDate.get(d.date) ?? 0,
    netPnl: fromMinorUnitsSigned(d.bookNetMinor),
    turnover: fromMinorUnits(d.turnoverMinor),
    bets: d.betsSettled ?? d.betsPlaced ?? 0,
  }))
}

function totalsFrom(
  rows: CommissionRowView[],
  daily: CommissionDailyPoint[],
): CommissionReport['totals'] {
  let earned = 0
  let paid = 0
  for (const r of rows) {
    if (r.direction === 'paid') paid = round2(paid + r.commission)
    else earned = round2(earned + r.commission)
  }
  return {
    commission: round2(earned + paid),
    earned,
    paid,
    turnover: round2(daily.reduce((s, d) => s + d.turnover, 0)),
    netPnl: round2(daily.reduce((s, d) => s + d.netPnl, 0)),
    bets: daily.reduce((s, d) => s + d.bets, 0),
  }
}

/** Live 200 → console report. */
export function toCommissionReport(
  live: AnalyticsCommissionResponse,
  daily?: AnalyticsDailyResponse,
): CommissionReport {
  const rows = (live.rows ?? []).map(toCommissionRowView)
  const commissionByDate = new Map<string, number>()
  for (const r of rows) {
    commissionByDate.set(r.date, round2((commissionByDate.get(r.date) ?? 0) + r.commission))
  }
  let series = dailyFromAnalytics(daily, commissionByDate)
  const known = new Set(series.map((d) => d.date))
  for (const [date, commission] of commissionByDate) {
    if (!known.has(date)) {
      series.push({ date, commission, netPnl: 0, turnover: 0, bets: 0 })
    }
  }
  series = series.sort((a, b) => a.date.localeCompare(b.date))

  return {
    asOf: live.asOf ?? daily?.asOf,
    martStale: Boolean(live.martStale),
    rows,
    eventRows: null,
    daily: series,
    totals: totalsFrom(rows, series),
    sportGameUnavailable: true,
  }
}

/** Live 503 Unavailable + optional daily for the book-net context line. */
export function unavailableCommissionReport(
  unavailable: AnalyticsUnavailable,
  daily?: AnalyticsDailyResponse,
): CommissionReport {
  const series = dailyFromAnalytics(daily, new Map())
  return {
    asOf: daily?.asOf,
    martStale: false,
    unavailable,
    rows: [],
    eventRows: null,
    daily: series,
    totals: {
      commission: 0,
      earned: 0,
      paid: 0,
      turnover: round2(series.reduce((s, d) => s + d.turnover, 0)),
      netPnl: round2(series.reduce((s, d) => s + d.netPnl, 0)),
      bets: series.reduce((s, d) => s + d.bets, 0),
    },
    sportGameUnavailable: true,
  }
}

/** Demo / unset analytics — keep event-level fixture table + sport/game mix. */
export function fromPnlFixture(pnl: PnlSummary): CommissionReport {
  const eventRows = pnl.rows.filter((r) => r.commission > 0)
  const daily: CommissionDailyPoint[] = pnl.daily.map((d) => ({
    date: d.date,
    commission: d.commission,
    netPnl: d.netPnl,
    turnover: d.turnover,
    bets: d.bets,
  }))
  return {
    asOf: pnl.asOf ?? new Date().toISOString(),
    martStale: pnl.martStale,
    rows: [],
    eventRows,
    daily,
    totals: {
      commission: pnl.totals.commission,
      earned: pnl.totals.commission,
      paid: 0,
      turnover: pnl.totals.turnover,
      netPnl: pnl.totals.netPnl,
      bets: pnl.totals.bets,
    },
    sportGameUnavailable: false,
  }
}

export { isUnavailable }
export type { AnalyticsUnavailable }
